/**
 * @file パンくずリスト。URLパスを分解して階層的なリンクを生成する。
 *        オプション名はRecoil stateから取得。confirm/complete等の末端パスはパンくずから除外する。
 */
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useRecoilValue } from 'recoil';
import { entryOptionInfoState } from '@/states/entryOptionInfoState';
import { quitOptionInfoState } from '@/states/quitOptionInfoState';
import { optionPageTitleState } from '@/states/optionTitleState';
import { providerInfoState } from '@/states/providerInfoState';
import { Constants } from '@/utils/constants';

// パンくずに表示する必要のない末端パス。確認画面・完了画面等の一時的なステップを除外する
const spliceList = ['confirm', 'complete', 'error', 'change', 'wcos'];

const BreadCrumbs = () => {
  const router = useRouter();
  const entryOptionInfo = useRecoilValue(entryOptionInfoState);
  const quitOptionInfo = useRecoilValue(quitOptionInfoState);
  const optionTitle = useRecoilValue(optionPageTitleState);
  const providerInfo = useRecoilValue(providerInfoState);

  // リンク先アドレスの取得
  const roots = [''];

  // 1. まず ? 以降（クエリ）と # 以降（ハッシュ）を完全に消す
  const cleanPath = decodeURI(router.asPath).replace(/[?#].*$/, '');

  // 2. 「/」で分割し、filter(Boolean) で空文字（先頭や末尾の / 分）を取り除く
  const paths = cleanPath.split('/').filter(Boolean);

  for (let i = 0; i < paths.length; i += 1) {
    // spliceListに含まれるパスはパンくずに不要なため除外
    if (spliceList.includes(paths[i])) {
      paths.splice(i, 1);
      break;
    }
    // pathセット
    roots.push(`${roots[i]}/${paths[i]}`);
  }

  // TOP画面ナビゲーションテキスト
  const topPageTxt = providerInfo.providerId === Constants.ProviderId.SNCC ? 'お知らせ' : 'メニュー';

  // ルートパスを日本語のページ名に変換。オプション名はRecoil stateから動的に取得する
  const toPageName = (path: string): string => {
    switch (path) {
      case 'payment':
        return 'お支払情報登録';
      case 'mail':
        return 'メールアドレスの登録';
      case 'notification':
        return '自動通知停止・再開';
      case 'id-password':
        return 'ID・パスワード変更';
      case 'credit-card':
        return 'お支払情報登録';
      case 'hikaritv':
        return 'ひかりTV';
      case 'billing':
        return 'ご利用金額明細';
      case 'invoice':
        return 'ご利用金額明細';
      case 'cashback':
        return 'キャッシュバック口座登録';
      case 'option-entry':
        return entryOptionInfo.option_name || optionTitle.optionTitle;
      case 'option-quit':
        return quitOptionInfo.option_name || optionTitle.optionTitle;
      case 'voip':
        return 'NURO光でんわ';
      case 'wc24':
        return 'ホワイトコール24申込書請求';
      case 'construction':
        return `${providerInfo.serviceName} 回線引込み工事承諾`;
      default:
        return path;
    }
  };

  return (
    <div className='bread'>
      {paths.includes('top') ? (
        topPageTxt
      ) : (
        <>
          <Link href='/top'>TOP</Link>
          {paths.map((val, idx) => (
            <span key={roots[idx]}>
              {' > '}
              <Link href={roots[idx + 1]}>{toPageName(val)}</Link>
            </span>
          ))}
        </>
      )}
    </div>
  );
};

export default BreadCrumbs;
